import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateFolder } from 'modules/documents/hooks/useCreateFolder'
import { SPACES } from 'modules/documents/lib/constants'
import { ALL_TYPES } from 'modules/documents/lib/constants'
import FeatureModalChecker, {
  CHECKED_FEATURES,
} from 'modules/timeoff/components/FeatureModalChecker'

interface FolderFormValues {
  name: string
}

interface Props {
  onClose: () => void
  space: string
  companyId: number
  parentFolderId?: number
  rootParentFolderId?: number
}

const NewFolderModal = ({
  onClose,
  space,
  companyId,
  parentFolderId,
  rootParentFolderId,
}: Props) => {
  const { createFolder } = useCreateFolder()

  const typeBySpace = () => {
    switch (space) {
      case SPACES.EMPLOYEE_MY_DOCUMENTS:
        return ALL_TYPES.custom
      case SPACES.COMPANY_INTERNAL:
        return ALL_TYPES.custom_company
      case SPACES.COMPANY_PUBLIC:
        return ALL_TYPES.custom_company_public
      default:
        throw new Error('Space does not have type')
    }
  }

  const form = useForm<FolderFormValues>({
    defaultValues: {
      name: '',
    },
    validate: (data) => {
      const errors: FormErrors<FolderFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createFolder({
        companyId: companyId,
        type: typeBySpace(),
        space: space,
        name: data.name,
        parentFolderId: parentFolderId,
        rootParentFolderId: rootParentFolderId,
      })

      const handled = handleResult(result, ({ name }) => {
        setErrors({
          name,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const disabled = form.isSubmitting || !form.isValid || !hasFeature('custom_folders')

  return CHECKED_FEATURES.create_custom_folders ? (
    <FeatureModalChecker feature={CHECKED_FEATURES.create_custom_folders} onClose={onClose}>
      <Modal
        illustration="new-folder"
        title={i18n.t('settings.folders.form.add.button')}
        description={i18n.t('settings.folders.form.add.root_folder_description')}
        onClose={onClose}
        onSubmit={() => form.handleSubmit()}
        footer={
          <Button disabled={disabled} submit>
            {i18n.t('settings.folders.form.add.title')}
          </Button>
        }
      >
        <TextField
          label={i18n.t('settings.folders.form.fields.name')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          readonly={!hasFeature('custom_folders')}
          error={form.touched.name ? form.errors.name : undefined}
        />
      </Modal>
    </FeatureModalChecker>
  ) : null
}

export default NewFolderModal
